import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function capitalize(
  str: null | string | undefined
): null | string | undefined {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function fileToBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== 'string') {
        resolve('');
        return;
      }

      const commaIndex = result.indexOf(',');
      resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Format a number as currency intelligently - shows decimals only if they exist
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted currency string (e.g., "$100" or "$100.50")
 */
export function formatCurrency(
  value: null | number | string | undefined,
  options?: {
    currency?: string;
    locale?: string;
    maxDecimals?: number;
    showNullAs?: string;
  }
): string {
  const {
    currency = 'USD',
    locale = 'en-US',
    maxDecimals = 2,
    showNullAs = '-'
  } = options || {};

  if (value === null || value === undefined) return showNullAs;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) return showNullAs;

  // Check if the number has a decimal component
  const hasDecimal = numValue % 1 !== 0;

  return new Intl.NumberFormat(locale, {
    currency,
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: hasDecimal ? 2 : 0,
    style: 'currency'
  }).format(numValue);
}

/**
 * Format file size intelligently - shows decimals only if they exist
 * @param bytes - The file size in bytes
 * @param options - Formatting options
 * @returns Formatted file size string (e.g., "1 KB", "1.5 MB")
 */
export function formatFileSize(
  bytes: number,
  options?: {
    maxDecimals?: number;
  }
): string {
  if (bytes === 0) return '0 Bytes';

  const { maxDecimals = 2 } = options || {};
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = bytes / Math.pow(k, i);

  // Only show decimals if they exist (consistent with other formatters)
  const hasDecimal = value % 1 !== 0;
  const formatted = hasDecimal
    ? parseFloat(value.toFixed(maxDecimals)).toString() // Remove trailing zeros
    : Math.round(value).toString();

  return `${formatted} ${sizes[i]}`;
}

/**
 * Format a number intelligently - shows decimals only if they exist
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted number string (e.g., "100" or "100.50")
 */
export function formatNumber(
  value: null | number | string | undefined,
  options?: {
    locale?: string;
    maxDecimals?: number;
    minDecimals?: number;
    prefix?: string;
    showNullAs?: string; // what to show for null/undefined values
    suffix?: string;
  }
): string {
  const { showNullAs = '' } = options || {};

  if (value === null || value === undefined || value === '') {
    return showNullAs;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return showNullAs || String(value);
  }

  const {
    locale,
    maxDecimals = 2,
    minDecimals = 0,
    prefix = '',
    suffix = ''
  } = options || {};

  // Check if the number has a decimal component
  const hasDecimal = numValue % 1 !== 0;

  const formatted = numValue.toLocaleString(locale, {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: hasDecimal ? minDecimals : 0
  });

  return `${prefix}${formatted}${suffix}`;
}

/**
 * Format a number as percentage intelligently
 * @param value - The number to format (e.g., 0.5 for 50% or 50 for 50%)
 * @param options - Formatting options
 * @returns Formatted percentage string (e.g., "50%" or "50.5%")
 *
 * @warning When using isDecimal: true, ensure the value is in decimal form (0-1 range).
 * Passing percentage values (e.g., 50) with isDecimal: true will produce incorrect results (5000%).
 */
export function formatPercent(
  value: null | number | string | undefined,
  options?: {
    isDecimal?: boolean; // if true, treats 0.5 as 50%; if false, treats 50 as 50%
    maxDecimals?: number;
    showNullAs?: string; // what to show for null/undefined values
  }
): string {
  const { showNullAs = '' } = options || {};

  if (value === null || value === undefined || value === '') {
    return showNullAs;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return showNullAs || String(value);
  }

  const { isDecimal = false, maxDecimals = 2 } = options || {};
  const percentValue = isDecimal ? numValue * 100 : numValue;

  return formatNumber(percentValue, { maxDecimals, suffix: '%' });
}
